// Identity/scope management is the other sanctioned reason to touch Sentry directly: these helpers
// centralize the `setUser`/`setTag` policy so call sites don't reach for `@sentry/react` themselves.
// oxlint-disable-next-line no-restricted-imports
import { setTag, setUser } from '@sentry/react';

/**
 * Aggregatable account tag. Set at the account route level (not on the `user` object) so dashboards
 * can answer "how many accounts are affected?" — the `user` field would overwrite the global identity.
 */
const ACCOUNT_ID_TAG = 'account_id';

/**
 * Auth0 subject of the current session. The post-login actions in `apps/auth-provider` report to the
 * same Sentry project under this identifier, so tagging it here is what lets a forced logout in the
 * browser be joined to the action execution that refused the token — the two are otherwise separate
 * events in separate traces, with no shared key.
 */
const AUTH0_SUBJECT_TAG = 'auth0.sub';

/**
 * Attaches the authenticated user's identity to every subsequent Sentry event. Set once at the
 * `_authenticated` route so errors on any authenticated route are attributed, and cleared on logout
 * via {@link clearSentryIdentity}.
 */
export function setSentryUser(user: { id: string }): void {
  setUser({ id: user.id });
}

/**
 * Tags subsequent events with the Auth0 subject of the current session, see {@link AUTH0_SUBJECT_TAG}.
 */
export function setSentryAuthSubject(sub: string): void {
  setTag(AUTH0_SUBJECT_TAG, sub);
}

/**
 * Detaches the Sentry user identity and account tag, e.g. on logout, so later events (or the next
 * user on a shared browser) are not attributed to the previous identity.
 */
export function clearSentryIdentity(): void {
  setUser(null);
  setTag(ACCOUNT_ID_TAG, undefined);
  setTag(AUTH0_SUBJECT_TAG, undefined);
}

/**
 * Tags subsequent events with the active account. Aggregatable in dashboards, unlike the `user` field.
 */
export function setSentryAccountTag(accountId: string): void {
  setTag(ACCOUNT_ID_TAG, accountId);
}

/**
 * Clears the active-account tag when leaving account scope, see {@link setSentryAccountTag}.
 */
export function clearSentryAccountTag(): void {
  setTag(ACCOUNT_ID_TAG, undefined);
}
